import { useState, useEffect, useContext } from 'react'
import { useNavigate, useParams } from "react-router-dom"
import moment from 'moment-timezone'

import _ from "lodash"

import { enqueueSnackbar } from 'notistack'

import Body from './body'

import { AppContext } from "/providers/app-context"

import loadTemplate from '/utils/template-loader'
import { saveAppleModel, saveAppleTemplate, saveGoogleData, saveVariables, saveMetaData, saveGoogleClass, saveGoogleObject, saveScannerConfiguration, uploadTemplatePreview } from '/providers/templates-api'
import Variables from '/utils/manage-variables'
import captureAppleFrontPreview from './apple/capture-front-preview'
import captureGoogleFrontPreview from './google/capture-front-preview'

let skipGoogleClassSave = false
let skipGoogleObjectSave = false

const stripDataUrlPrefix = dataUrl => dataUrl.replace(/^data:image\/png;base64,/, "")


const Editor = () => {
  const { apiSource, apiKey, user, setUser, refreshTemplates } = useContext(AppContext)

  const navigate = useNavigate()
  const { templateId } = useParams()

  const [templateLoaded, setTemplateLoaded] = useState(false)

  const [smartixPassType, setSmartixPassType] = useState(null)
  const [applePassType, setApplePassType] = useState(null)
  const [appleSubType, setAppleSubType] = useState(null)
  const [googlePassType, setGooglePassType] = useState(null)
  const [googleSubType, setGoogleSubType] = useState(null)

  const [appleModel, setAppleModel] = useState()
  const [appleTemplate, setAppleTemplate] = useState()
  const [googleBoth, setGoogleBoth] = useState()
  const [googleClass, setGoogleClass] = useState()
  const [googleObject, setGoogleObject] = useState()
  const [variables, setVariables] = useState({})
  const [scannerData, setScannerData] = useState({})
  const [isManagedPassTemplate, setisManagedPassTemplate] = useState(false)
  const [passGroupId, setPassGroupId] = useState(null)
  const [variableCatalog, setVariableCatalog] = useState([])
  const [templateClientId, setTemplateClientId] = useState(null)
  const [applePreviewVersion, setApplePreviewVersion] = useState(Date.now())
  const [metaData, setMetaData] = useState({})
  const [classes, setClasses] = useState("")
  const [showVariables, setShowVariables] = useState(false)

  const saveApplePreview = async () => {
    const parsedTemplateClientId = Number(templateClientId)
    const shouldUploadApplePreview = parsedTemplateClientId === 0

    console.log("saveApplePreview: gate", {
      templateId,
      templateClientId,
      parsedTemplateClientId,
      shouldUploadApplePreview
    })

    if (!shouldUploadApplePreview) return { ok: true, skipped: true }

    const dataUrl = await captureAppleFrontPreview()
    const base64Data = stripDataUrlPrefix(dataUrl)

    const result = await uploadTemplatePreview({ apiSource, apiKey, templateId, base64Data, platform: "apple", ownerClientId: templateClientId })
    if (result?.ok) setApplePreviewVersion(Date.now())
    return result
  }

  const saveGooglePreview = async () => {
    const parsedTemplateClientId = Number(templateClientId)
    const shouldUploadGooglePreview = parsedTemplateClientId === 0

    console.log("saveGooglePreview: gate", {
      templateId,
      templateClientId,
      parsedTemplateClientId,
      shouldUploadGooglePreview
    })

    if (!shouldUploadGooglePreview) return { ok: true, skipped: true }

    const dataUrl = await captureGoogleFrontPreview()
    const base64Data = stripDataUrlPrefix(dataUrl)

    const result = await uploadTemplatePreview({ apiSource, apiKey, templateId, base64Data, platform: "google", ownerClientId: templateClientId })
    console.log("saveGooglePreview: upload result", { templateId, result })
    return result
  }

  // Prevent hot refresh from causing data saves
  if (import.meta.hot) {
    import.meta.hot.on('vite:beforeUpdate', () => {
      setTemplateLoaded(false)
    })
    import.meta.hot.on('vite:afterUpdate', () => {
      setTemplateLoaded(true)
    })
  }

  useEffect(() => {
    if (templateId === null) navigate("/templates", { replace: true })

    loadInitialData()

    if (user.preferences.highlight_editable_items) {
      let u = _.cloneDeep(user)
      u.preferences.highlight_editable_items = false
      setUser(u)

      setTimeout(() => {
        let u = _.cloneDeep(user)
        u.preferences.highlight_editable_items = true
        setUser(u)
      }, 2000) // Turn off then on again to ensure all fields change colour at same time.
    }
  }, [])

  useEffect(() => {
    setClasses(user.preferences.highlight_editable_items ? "showEditFields h-full w-full" : "h-full w-full")
  }, [user.preferences.highlight_editable_items])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        try {
          enqueueSnackbar({ message: 'Saving Apple model', variant: 'light' })
          let result = await saveAppleModel({ apiSource, apiKey, templateId, appleModel })

          if (!result.ok) throw result.error ?? new Error("Apple model save failed")

          const previewResult = await saveApplePreview()

          if (!previewResult.ok) throw previewResult.error ?? new Error("Template preview upload failed")

          enqueueSnackbar({ message: 'Apple model saved', variant: 'success' })
          refreshTemplates()
        }
        catch (err) {
          console.error(err)
          enqueueSnackbar({ message: 'Apple model save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [appleModel])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        try {
          enqueueSnackbar({ message: 'Saving Apple template', variant: 'light' })

          let result = await saveAppleTemplate({ apiSource, apiKey, templateId, appleTemplate })

          if (!result.ok) throw result.error ?? new Error("Apple template save failed")

          const previewResult = await saveApplePreview()

          if (!previewResult.ok) throw previewResult.error ?? new Error("Template preview upload failed")

          enqueueSnackbar({ message: 'Apple template saved', variant: 'success' })
          refreshTemplates()
        }
        catch (err) {
          console.error(err)
          enqueueSnackbar({ message: 'Apple template save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [appleTemplate])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving Google class', variant: 'light' })

        let result = await saveGoogleClass({ apiSource, apiKey, templateId, googleClass })

        if (result.ok) {
          const previewResult = await saveGooglePreview()
          if (!previewResult.ok) enqueueSnackbar({ message: 'Google preview upload failed', variant: 'error' })

          enqueueSnackbar({ message: 'Google class saved', variant: 'success' })

          skipGoogleClassSave = true
          setGoogleClass(result.classData)

          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Google class save failed', variant: 'error' })
        }
      }
      if (skipGoogleClassSave) skipGoogleClassSave = false
      else save()
    }
  }, [googleClass])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving Google object', variant: 'light' })

        let result = await saveGoogleObject({ apiSource, apiKey, templateId, googleObject })

        if (result.ok) {
          const previewResult = await saveGooglePreview()
          if (!previewResult.ok) enqueueSnackbar({ message: 'Google preview upload failed', variant: 'error' })

          enqueueSnackbar({ message: 'Google object saved', variant: 'success' })

          skipGoogleObjectSave = true
          setGoogleObject(result.objectData)

          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Google object save failed', variant: 'error' })
        }
      }
      if (skipGoogleObjectSave) skipGoogleObjectSave = false
      else save()
    }
  }, [googleObject])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving Google class & object', variant: 'light' })

        let googleClass = googleBoth.googleClass
        let googleObject = googleBoth.googleObject

        let result = await saveGoogleData({ apiSource, apiKey, templateId, googleClass, googleObject })

        if (result.ok) {
          const previewResult = await saveGooglePreview()
          if (!previewResult.ok) enqueueSnackbar({ message: 'Google preview upload failed', variant: 'error' })

          enqueueSnackbar({ message: 'Google class & object saved', variant: 'success' })

        // Ensure we do not fire the useEffects for googleClass and googleObject
          skipGoogleClassSave = true
          skipGoogleObjectSave = true
          setGoogleClass(result.classData)
          setGoogleObject(result.objectData)

          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Google class & object save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [googleBoth])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {

        const groupingId = (_.get(variables, "pass.grouping.id") ?? "").replace(/[ ]/g,"")
        const groupingIndex = _.get(variables, "pass.grouping.index")
        if (!Number.isInteger(groupingIndex) || !groupingId.length) _.set(variables,"pass.grouping.index",null)

        // Check if pass.validity values are valid date strings
        const startDate = _.get(variables, "pass.validity.start")
        if (startDate) {
          const startMoment = moment(startDate,"YYYYMMDDHHmmSS")
          if (!startMoment.isValid()) _.set(variables,"pass.validity.start",null)
        }

        const endDate = _.get(variables, "pass.validity.end")
        if (endDate) {
          const endMoment = moment(endDate,"YYYYMMDDHHmmSS")
          if (!endMoment.isValid()) _.uet(variables,"pass.validity.end",null)
        }

        enqueueSnackbar({ message: 'Saving instance data', variant: 'light' })

        const customVariables = variableCatalog
          .filter(item => item.var_name?.startsWith("custom."))
          .map(item => ({
            var_name: item.var_name,
            encrypted: Number(item.encrypted) === 1 ? 1 : 0
          }))

        let result = await saveVariables({ apiSource, apiKey, templateId, variables, customVariables })

        if (result.ok) {
          const applePreviewResult = await saveApplePreview()
          if (!applePreviewResult.ok) enqueueSnackbar({ message: 'Apple preview upload failed', variant: 'error' })

          const googlePreviewResult = await saveGooglePreview()
          if (!googlePreviewResult.ok) enqueueSnackbar({ message: 'Google preview upload failed', variant: 'error' })

          enqueueSnackbar({ message: 'Instance data saved', variant: 'success' })
          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Instance data save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [variables])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving scanner data', variant: 'light' })

        let result = await saveScannerConfiguration({ apiSource, apiKey, templateId, scannerData })

        if (result.ok) {
          enqueueSnackbar({ message: 'Scanner data saved', variant: 'success' })
          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Scanner data save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [scannerData])

  useEffect(() => {
    if (templateLoaded) {
      const save = async () => {
        enqueueSnackbar({ message: 'Saving meta data', variant: 'light' })

        let result = await saveMetaData({ apiSource, apiKey, templateId, metaData })

        if (result.ok) {
          enqueueSnackbar({ message: 'Meta data saved', variant: 'success' })
          refreshTemplates()
        } else {
          enqueueSnackbar({ message: 'Meta data save failed', variant: 'error' })
        }
      }
      save()
    }
  }, [metaData])

  async function loadInitialData() {

    const { metaData, isManagedPassTemplate, smartixPassType, appleModel, appleTemplate, googleObject, variables, scannerData, googleClass, applePassType, appleSubType, googlePassType, googleSubType, passGroupId, variableCatalog, templateClientId } = await loadTemplate({ apiSource, apiKey, templateId })

    setSmartixPassType(smartixPassType)
    setApplePassType(applePassType)
    setAppleSubType(appleSubType)
    setGooglePassType(googlePassType)
    setGoogleSubType(googleSubType)
    setisManagedPassTemplate(isManagedPassTemplate)
    setTemplateClientId(templateClientId)
    setPassGroupId(passGroupId)
    setVariableCatalog(variableCatalog)

    setAppleModel(appleModel)
    setAppleTemplate(appleTemplate)
    setGoogleClass(googleClass)
    setGoogleObject(googleObject)
    setVariables(variables)
    setMetaData(metaData)
    setScannerData(scannerData)

    // Delay setting this to true until next render cycle to ensure above 
    // useEffects do not pick up our initial load as a change
    setTimeout(e => setTemplateLoaded(true), 100)
  }

  const openVariablesManager = () => setShowVariables(true)

  return (
    <>
      <div className={classes}>
        <Body
          templateId={templateId}
          smartixPassType={smartixPassType}
          variableCatalog={variableCatalog}
          appleTemplate={appleTemplate}
          googleObject={googleObject}
          variables={variables}
          setVariables={setVariables}
          scannerData={scannerData}
          setScannerData={setScannerData}
          appleModel={appleModel}
          googleClass={googleClass}
          applePassType={applePassType}
          appleSubType={appleSubType}
          googlePassType={googlePassType}
          templateLoaded={templateLoaded}
          googleSubType={googleSubType}
          setGoogleBoth={setGoogleBoth}
          setAppleModel={setAppleModel}
          setAppleTemplate={setAppleTemplate}
          setGoogleClass={setGoogleClass}
          setGoogleObject={setGoogleObject}
          isManagedPassTemplate={isManagedPassTemplate}
          metaData={metaData}
          setMetaData={setMetaData}
          openVariablesManager={openVariablesManager}
          applePreviewVersion={applePreviewVersion}
        />
      </div>

      <Variables
        open={showVariables}
        close={() => setShowVariables(false)}
        variables={variables}
        setVariables={setVariables}
        catalogItems={variableCatalog}
        setCatalogItems={setVariableCatalog}
        isManagedPassTemplate={isManagedPassTemplate}
        passGroupId={passGroupId}
        appleModel={appleModel}
        appleTemplate={appleTemplate}
        googleClass={googleClass}
        googleObject={googleObject}
      />
    </>
  )
}

export default Editor
